'use client';

import clsx from 'clsx';
import { useState } from 'react';

import Accordion, { type AccordionItem } from '@commons/components/Accordion';
import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Text from '@commons/components/Text';
import Texture from '@commons/components/Texture';
import Flex from '@commons/containers/Flex';
import { breakpoints } from '@commons/contexts/responsive';
import type { DrupalBlock, DrupalImage as DrupalImageType } from '@commons/types/drupal';

import styles from './ImagesAccordion.module.scss';

/**
 * Next.js Image `sizes` property for the ImagesAccordion component.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

const IMAGE_SIZES = `(max-width: ${breakpoints.lg}px) 100vw, (max-width: ${breakpoints.xl}px) 460px, 640px`;

// Type definition for the item properties.

type Item = {
  title: string;
  image: DrupalImageType;
  content: string;
};

// Type definition for the ImagesAccordion block component properties.

type Props = DrupalBlock<{
  items: Item[];
}>;

/**
 * ImagesAccordion block component.
 */

const ImagesAccordionBlock = ({ content }: Props) => {
  const { items = [] } = content;
  const [activeIndex, setActiveIndex] = useState(0);

  const hasItems = items.length > 0;

  const accordionItems: AccordionItem[] = items.map((item) => ({
    title: (
      <Text level="h4" marginBottom="none">
        {item.title}
      </Text>
    ),
    children: <RichText width={12} value={item.content} />,
  }));

  const handleTriggerClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <Flex width={12} gap="md" direction={{ xs: 'column', lg: 'row' }} className={styles.wrapper}>
      <Flex
        width={{ xs: 12, lg: 6 }}
        align="center"
        theme="grey-10"
        justify="center"
        padding={{ xs: 'md', lg: 'xxl' }}
        className={clsx(styles.imgWrapper, 'h-100')}
      >
        <Texture />
        {hasItems && (
          <DrupalImage
            {...items[activeIndex].image}
            sizes={IMAGE_SIZES}
            className={styles.image}
            key={activeIndex}
          />
        )}
      </Flex>
      <Flex.Item width={{ xs: 12, lg: 6 }}>
        <Accordion
          items={accordionItems}
          collapsible={false}
          defaultValue="item-0"
          className="w-100"
          onTriggerClick={handleTriggerClick}
        />
      </Flex.Item>
    </Flex>
  );
};

export default ImagesAccordionBlock;
